
import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { AssessmentQuestion, AssessmentResponse, AssessmentResult } from '@/types/assessment';
import { getQuestions, saveResponses, getResultsForUser, generateReport } from '@/utils/assessmentUtils';
import { getOpenAIKey, saveOpenAIKey } from '@/utils/apiKeyUtils';
import { updateLastActivityTimestamp } from '@/utils/auth/sessionService';

interface DatabaseContextProps {
  getQuestions: () => AssessmentQuestion[];
  saveResponses: (userId: string, responses: AssessmentResponse[]) => Promise<void>;
  generateReport: (userId: string, responses: AssessmentResponse[]) => Promise<AssessmentResult>;
  getResultsForUser: (userId: string) => Promise<AssessmentResult[]>;
  getOpenAIKey: () => Promise<string>;
  saveOpenAIKey: (key: string) => Promise<void>;
  isConnected: boolean;
}

const DatabaseContext = createContext<DatabaseContextProps | undefined>(undefined);

export const DatabaseProvider = ({ children }: { children: ReactNode }) => {
  // Check if we have a valid Supabase connection
  const isConnected = true; // We now have a valid connection
  
  // Update activity timestamp on database operations
  useEffect(() => {
    // Initial activity timestamp set
    updateLastActivityTimestamp();
    
    // Set up activity listener for user interactions
    const handleUserActivity = () => {
      updateLastActivityTimestamp();
    };
    
    // Attach to common user activity events
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);
    window.addEventListener('scroll', handleUserActivity);
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, []);

  return (
    <DatabaseContext.Provider
      value={{
        getQuestions,
        saveResponses,
        generateReport,
        getResultsForUser,
        getOpenAIKey,
        saveOpenAIKey,
        isConnected,
      }}
    >
      {children}
    </DatabaseContext.Provider>
  );
};

export const useDatabase = () => {
  const context = useContext(DatabaseContext);
  if (context === undefined) {
    throw new Error('useDatabase must be used within a DatabaseProvider');
  }
  
  // Update activity timestamp when using database
  updateLastActivityTimestamp();
  
  return context;
};
